import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RtkParticipantsAudio } from "@cloudflare/realtimekit-react-ui";
import VideoTile from "@/components/studio/VideoTile";
import { CountdownPill } from "@/components/studio/Countdown";

/**
 * ProgramCanvas — the single source of truth for what the audience sees.
 *
 * It is rendered in two places with IDENTICAL output:
 *   1. The producer console (scaled-down preview, no audio)
 *   2. The Program Output page that Cloudflare's headless recorder captures
 *      (full viewport, with audio via <RtkParticipantsAudio/>)
 *
 * Everything is drawn on a fixed 1280x720 stage that is CSS-scaled to fit its
 * container, so layouts, lower-thirds and banners look the same everywhere.
 */
export const STAGE_W = 1280;
export const STAGE_H = 720;

const THEMES = {
  copper: {
    bg: "linear-gradient(135deg, #061a33 0%, #0a2647 55%, #1a1208 100%)",
    accent: "#c9772e",
    accentSoft: "rgba(201,119,46,0.18)",
    panel: "rgba(6, 20, 40, 0.82)",
    text: "#f5f7fa",
    subtext: "#d9c1a8",
  },
  midnight: {
    bg: "linear-gradient(135deg, #05070c 0%, #0a0f17 60%, #0d1b2e 100%)",
    accent: "#3b8bd4",
    accentSoft: "rgba(59,139,212,0.18)",
    panel: "rgba(5, 8, 14, 0.85)",
    text: "#f5f7fa",
    subtext: "#a9c4e0",
  },
  clean: {
    bg: "linear-gradient(135deg, #f5f7fa 0%, #e6ecf3 100%)",
    accent: "#c9772e",
    accentSoft: "rgba(201,119,46,0.14)",
    panel: "rgba(255,255,255,0.92)",
    text: "#0f1c33",
    subtext: "#4a5a70",
  },
};

/** Compute tile rectangles (in stage px) for a layout and N sources. */
export function layoutRects(layout, n) {
  const PAD = 40, GAP = 24;
  if (n <= 0) return [];
  if (layout === "solo" || layout === "speaker" || n === 1) {
    return [{ x: 0, y: 0, w: STAGE_W, h: STAGE_H, radius: 0 }];
  }
  if (layout === "pip") {
    const iw = 352, ih = 198;
    return [
      { x: 0, y: 0, w: STAGE_W, h: STAGE_H, radius: 0 },
      { x: STAGE_W - iw - PAD, y: PAD, w: iw, h: ih, radius: 14 },
    ];
  }
  // split / grid
  const cols = layout === "split" || n === 2 ? 2 : n <= 4 ? 2 : 3;
  const rows = Math.ceil(Math.min(n, 6) / cols);
  const cellW = (STAGE_W - PAD * 2 - GAP * (cols - 1)) / cols;
  const cellH = (STAGE_H - PAD * 2 - GAP * (rows - 1)) / rows;
  // keep 16:9 inside each cell
  let w = cellW, h = (cellW * 9) / 16;
  if (h > cellH) { h = cellH; w = (cellH * 16) / 9; }
  const totalW = cols * w + GAP * (cols - 1);
  const totalH = rows * h + GAP * (rows - 1);
  const x0 = (STAGE_W - totalW) / 2;
  const y0 = (STAGE_H - totalH) / 2;
  const rects = [];
  for (let i = 0; i < Math.min(n, 6); i += 1) {
    const c = i % cols, r = Math.floor(i / cols);
    // center the last row when it's not full
    const inRow = Math.min(cols, Math.min(n, 6) - r * cols);
    const rowW = inRow * w + GAP * (inRow - 1);
    const rx0 = (STAGE_W - rowW) / 2;
    rects.push({ x: (r === rows - 1 ? rx0 : x0) + c * (w + GAP), y: y0 + r * (h + GAP), w, h, radius: 18 });
  }
  return rects;
}

function useFitScale(containerRef) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const s = Math.min(r.width / STAGE_W, r.height / STAGE_H) || 1;
      setScale(s);
      setOffset({ x: (r.width - STAGE_W * s) / 2, y: (r.height - STAGE_H * s) / 2 });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [containerRef]);
  return { scale, offset };
}

function LogoBug({ theme }) {
  return (
    <div className="absolute flex items-center gap-3" style={{ top: 36, right: 44 }} data-testid="program-logo">
      <img src="/logo-mark.png" alt="" style={{ width: 64, height: 64, filter: "drop-shadow(0 4px 12px rgba(0,0,0,.35))" }} />
      <div style={{ lineHeight: 1.05 }}>
        <div style={{ color: theme.text, fontWeight: 700, fontSize: 22, letterSpacing: 2 }}>FRIDAY</div>
        <div style={{ color: theme.accent, fontWeight: 600, fontSize: 12, letterSpacing: 4 }}>CONVERSATIONS</div>
      </div>
    </div>
  );
}

const PLATE_KEYFRAMES = `
@keyframes fcPlateInLeft{from{opacity:0;transform:translateX(-64px)}to{opacity:1;transform:none}}
@keyframes fcPlateOutLeft{from{opacity:1;transform:none}to{opacity:0;transform:translateX(-40px)}}
@keyframes fcPlateInRight{from{opacity:0;transform:translateX(64px)}to{opacity:1;transform:none}}
@keyframes fcPlateOutRight{from{opacity:1;transform:none}to{opacity:0;transform:translateX(40px)}}
@keyframes fcPlateInUp{from{opacity:0;transform:translateY(36px)}to{opacity:1;transform:none}}
@keyframes fcPlateOutDown{from{opacity:1;transform:none}to{opacity:0;transform:translateY(24px)}}
@keyframes fcPlateSub{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@keyframes fcPlateBar{from{transform:scaleY(0)}to{transform:scaleY(1)}}
@keyframes fcPlateLine{from{transform:scaleX(0)}to{transform:scaleX(1)}}
`;

function plateBody(style, c, theme, size, position) {
  const big = size === "large";
  const nameFs = big ? 42 : 34;
  const subFs = big ? 25 : 20;
  const sub = [c.title, c.org].filter(Boolean).join(" · ");
  const subEl = sub ? <div style={{ animation: "fcPlateSub .45s .15s cubic-bezier(.2,.8,.2,1) both" }} data-testid="program-lower-third-sub">{sub}</div> : null;
  if (style === "pill") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 18, background: theme.panel, borderRadius: 999, padding: big ? "16px 34px 16px 22px" : "12px 28px 12px 18px", backdropFilter: "blur(6px)", boxShadow: "0 12px 40px rgba(0,0,0,.35)" }}>
        <div style={{ width: big ? 18 : 14, height: big ? 18 : 14, borderRadius: 999, background: theme.accent, boxShadow: `0 0 0 6px ${theme.accentSoft}` }} />
        <div>
          <div style={{ color: theme.text, fontWeight: 700, fontSize: nameFs, lineHeight: 1.1, whiteSpace: "nowrap" }}>{c.name}</div>
          {subEl && <div style={{ color: theme.subtext, fontWeight: 500, fontSize: subFs, marginTop: 2, whiteSpace: "nowrap" }}>{subEl}</div>}
        </div>
      </div>
    );
  }
  if (style === "minimal") {
    const shadow = "0 2px 12px rgba(0,0,0,.55), 0 0 2px rgba(0,0,0,.8)";
    const align = position === "center" ? "center" : position === "right" ? "right" : "left";
    const lineMargin = align === "center" ? "10px auto 8px" : align === "right" ? "10px 0 8px auto" : "10px 0 8px";
    return (
      <div style={{ textAlign: align }}>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: nameFs + 4, lineHeight: 1.05, whiteSpace: "nowrap", textShadow: shadow }}>{c.name}</div>
        <div style={{ width: 72, height: 4, background: theme.accent, margin: lineMargin, transformOrigin: align === "right" ? "right" : align === "center" ? "center" : "left", animation: "fcPlateLine .4s .1s cubic-bezier(.2,.8,.2,1) both" }} />
        {subEl && <div style={{ color: "#f1f1f1", fontWeight: 500, fontSize: subFs, whiteSpace: "nowrap", textShadow: shadow }}>{subEl}</div>}
      </div>
    );
  }
  if (style === "broadcast") {
    return (
      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start" }}>
        <div style={{ background: theme.accent, color: "#fff", fontWeight: 800, fontSize: nameFs, lineHeight: 1.1, padding: big ? "12px 30px" : "10px 24px", borderRadius: "8px 8px 0 0", whiteSpace: "nowrap", letterSpacing: 0.5 }}>{c.name}</div>
        {subEl && <div style={{ background: theme.panel, color: theme.text, fontWeight: 500, fontSize: subFs, padding: big ? "10px 30px 12px" : "8px 24px 10px", borderRadius: "0 0 8px 8px", whiteSpace: "nowrap", backdropFilter: "blur(6px)", minWidth: "100%" }}>{subEl}</div>}
      </div>
    );
  }
  // classic
  return (
    <div style={{ display: "flex", alignItems: "stretch", overflow: "hidden" }}>
      <div style={{ width: 10, background: theme.accent, borderRadius: "6px 0 0 6px", transformOrigin: "bottom", animation: "fcPlateBar .35s cubic-bezier(.2,.8,.2,1) both" }} />
      <div style={{ background: theme.panel, padding: big ? "16px 34px 18px 26px" : "14px 28px 16px 22px", borderRadius: "0 10px 10px 0", backdropFilter: "blur(6px)" }}>
        <div style={{ color: theme.text, fontWeight: 700, fontSize: nameFs, lineHeight: 1.1, whiteSpace: "nowrap" }}>{c.name}</div>
        {subEl && <div style={{ color: theme.subtext, fontWeight: 500, fontSize: subFs, marginTop: 4, whiteSpace: "nowrap" }}>{subEl}</div>}
      </div>
    </div>
  );
}

/** Name plate with slide-in / slide-out. Keeps the last content around while it animates out. */
function LowerThird({ lt, plate, autoSeconds = 6, theme, bottom, questionOn }) {
  // Client-side safety net: an auto plate also expires locally, so a backend restart can never
  // leave one stuck on screen. The countdown starts when THIS device first sees the flash
  // (not from the server's shown_at) so a laptop/TV clock that runs a few seconds ahead can't
  // make plates vanish early or never appear.
  const [, retick] = useState(0);
  const seenRef = useRef({ key: null, at: 0 });
  const flashKey = lt?.auto && lt?.visible ? `${lt.shown_at || ""}|${lt.participant_id || ""}|${lt.name || ""}` : null;
  if (flashKey && seenRef.current.key !== flashKey) seenRef.current = { key: flashKey, at: Date.now() };
  const deadline = flashKey ? seenRef.current.at + (autoSeconds + 1.5) * 1000 : null;
  const expired = deadline ? Date.now() > deadline : false;
  useEffect(() => {
    if (!deadline || expired) return undefined;
    const id = setTimeout(() => retick((t) => t + 1), Math.max(50, deadline - Date.now()));
    return () => clearTimeout(id);
  }, [deadline, expired]);
  const active = !!(lt?.visible && !expired && (lt.name || lt.title || lt.org));
  const [phase, setPhase] = useState(active ? "in" : "off"); // in | out | off
  const contentRef = useRef(active ? lt : null);
  if (active) contentRef.current = lt;
  useEffect(() => {
    if (active) { setPhase("in"); return undefined; }
    setPhase((p) => (p === "in" ? "out" : p));
    const id = setTimeout(() => setPhase("off"), 450);
    return () => clearTimeout(id);
  }, [active]);
  const c = contentRef.current;
  if (phase === "off" || !c) return null;
  const style = plate?.style || "classic";
  // The question card lives bottom-right; keep the plate clear of it.
  const position = questionOn && plate?.position !== "left" ? "left" : (plate?.position || "left");
  const size = plate?.size || "normal";
  const anim = phase === "in"
    ? { left: "fcPlateInLeft", right: "fcPlateInRight", center: "fcPlateInUp" }[position]
    : { left: "fcPlateOutLeft", right: "fcPlateOutRight", center: "fcPlateOutDown" }[position];
  const justify = { left: "flex-start", right: "flex-end", center: "center" }[position];
  return (
    <div className="absolute flex" style={{ left: 48, right: 48, bottom, justifyContent: justify, pointerEvents: "none" }} data-testid="program-lower-third" data-phase={phase} data-style={style} data-position={position}>
      <div key={`${c.participant_id || ""}|${c.name}|${c.title}|${c.org}`} style={{ maxWidth: 820, animation: `${anim} ${phase === "in" ? ".5s" : ".4s"} cubic-bezier(.2,.8,.2,1) both` }}>
        {plateBody(style, c, theme, size, position)}
      </div>
    </div>
  );
}

function Banner({ banner, theme }) {
  if (!banner?.visible || !banner.text) return null;
  return (
    <div className="absolute left-0 right-0 flex items-center" style={{ bottom: 0, height: 60, background: theme.panel, borderTop: `3px solid ${theme.accent}` }} data-testid="program-banner">
      <div style={{ background: theme.accent, color: "#fff", fontWeight: 700, fontSize: 14, letterSpacing: 3, padding: "0 22px", height: "100%", display: "flex", alignItems: "center" }}>
        LIVE
      </div>
      <div style={{ color: theme.text, fontWeight: 600, fontSize: 24, padding: "0 28px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{banner.text}</div>
    </div>
  );
}

function QuestionCard({ q, theme, bottom }) {
  if (!q?.visible || !q.text) return null;
  return (
    <div className="absolute" style={{ right: 48, bottom, width: 520, background: theme.panel, borderLeft: `8px solid ${theme.accent}`, borderRadius: 12, padding: "18px 24px", backdropFilter: "blur(6px)" }} data-testid="program-question">
      <div style={{ color: theme.accent, fontWeight: 700, fontSize: 13, letterSpacing: 3 }}>AUDIENCE QUESTION</div>
      <div style={{ color: theme.text, fontWeight: 600, fontSize: 24, lineHeight: 1.25, marginTop: 6 }}>{q.text}</div>
      {q.author && <div style={{ color: theme.subtext, fontSize: 16, marginTop: 8 }}>— {q.author}</div>}
    </div>
  );
}

function HoldingCard({ theme, title, subtitle, startTime, countdownTo }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center" data-testid="program-holding">
      <img src="/logo-mark.png" alt="" style={{ width: 168, height: 168, filter: "drop-shadow(0 12px 30px rgba(0,0,0,.4))" }} />
      <div style={{ color: theme.text, fontWeight: 700, fontSize: 30, letterSpacing: 5, marginTop: 28 }}>FRIDAY CONVERSATIONS</div>
      <div style={{ color: theme.accent, fontWeight: 600, fontSize: 40, marginTop: 10, maxWidth: 1000, lineHeight: 1.15 }} data-testid="holding-headline">{title}</div>
      <div style={{ color: theme.subtext, fontSize: 22, marginTop: 14, maxWidth: 1100, lineHeight: 1.3 }} data-testid="holding-message">{subtitle}</div>
      {countdownTo && <CountdownPill targetIso={countdownTo} theme={theme} />}
      {startTime && (
        <div style={{ color: theme.text, fontSize: 22, fontWeight: 600, marginTop: countdownTo ? 16 : 26, letterSpacing: 1 }} data-testid="holding-start-time">{startTime}</div>
      )}
    </div>
  );
}

/**
 * props:
 *  meeting        RealtimeKit client (needed for audio + speaker layout)
 *  roster         from useRoster(meeting): { byCustomId, list }
 *  activeSpeaker  peerId of the active speaker (optional)
 *  program        show.program
 *  participants   show.participants (registry, for names)
 *  showTitle      string
 *  withAudio      render remote audio (ONLY on the recorder page)
 *  className      wrapper classes (container decides the size)
 */
export default function ProgramCanvas({ meeting, roster, activeSpeaker, program, participants = [], showTitle, scheduledFor, withAudio = false, onFlow, className = "" }) {
  const containerRef = useRef(null);
  const { scale, offset } = useFitScale(containerRef);
  const theme = THEMES[program?.theme] || THEMES.copper;
  const byId = useMemo(() => new Map((participants || []).map((p) => [p.participant_id, p])), [participants]);

  // Resolve on-air sources -> connected RealtimeKit participants.
  const sources = useMemo(() => {
    const ids = program?.sources || [];
    const out = [];
    const byCustom = roster?.byCustomId || new Map();
    if (program?.layout === "speaker") {
      if (!ids.length) return out;
      const list = roster?.list || [];
      const spk = list.find((e) => e.peerId === activeSpeaker && e.videoEnabled)
        || list.find((e) => ids.includes(e.customId) && e.videoEnabled)
        || list.find((e) => e.videoEnabled)
        || null;
      if (spk) out.push(spk);
      return out;
    }
    ids.forEach((id) => { const e = byCustom.get(id); if (e) out.push(e); });
    return out;
  }, [program, roster, activeSpeaker]);

  const rects = layoutRects(program?.layout || "solo", sources.length);
  const bannerOn = !!(program?.banner?.visible && program?.banner?.text);
  const overlayBottom = bannerOn ? 60 + 28 : 48;

  // Picture-flow summary for watchdogs (room monitor): how many tiles expect frames vs. how many
  // are really getting them. Reported on every tile tick (2 s) and whenever the on-air set changes.
  const flows = useRef(new Map());
  const flowCb = useRef(onFlow);
  flowCb.current = onFlow;
  const expecting = sources.filter((s) => s.videoEnabled).length;
  const report = useCallback(() => {
    if (!flowCb.current) return;
    let flowing = 0;
    flows.current.forEach((f) => { if (f && f.fps > 0 && f.stalledMs < 2500) flowing += 1; });
    flowCb.current({ tiles: expecting, flowing, at: Date.now() });
  }, [expecting]);
  useEffect(() => {
    const keep = new Set(sources.map((s) => s.peerId));
    [...flows.current.keys()].forEach((k) => { if (!keep.has(k)) flows.current.delete(k); });
    report();
  }, [sources, report]);
  const tileFlow = (peerId) => (onFlow ? (f) => { flows.current.set(peerId, f); report(); } : undefined);

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`} style={{ background: "#000" }} data-testid="program-canvas">
      <div
        style={{
          position: "absolute", left: offset.x, top: offset.y, width: STAGE_W, height: STAGE_H,
          transform: `scale(${scale})`, transformOrigin: "top left", background: theme.bg,
          fontFamily: "Poppins, ui-sans-serif, system-ui, sans-serif",
        }}
      >
        {sources.length === 0 ? (
          <HoldingCard theme={theme} title={program?.holding?.headline || showTitle || "Live Podcast"} subtitle={program?.holding?.message || "We'll be right with you"} startTime={program?.holding?.start_time || ""} countdownTo={program?.holding?.countdown === false ? null : scheduledFor || null} />
        ) : (
          sources.slice(0, rects.length).map((src, i) => {
            const r = rects[i];
            const reg = byId.get(src.customId);
            return (
              <div key={src.peerId} className="absolute" style={{ left: r.x, top: r.y, width: r.w, height: r.h, borderRadius: r.radius, overflow: "hidden", boxShadow: r.radius ? "0 18px 50px rgba(0,0,0,.45)" : "none", border: r.radius ? `2px solid ${theme.accentSoft}` : "none" }}>
                <VideoTile rtk={src.rtk} name={reg?.name || src.name} fx={program?.source_fx?.[src.customId]} onFlow={tileFlow(src.peerId)} className="h-full w-full" />
              </div>
            );
          })
        )}

        {program?.show_logo !== false && <LogoBug theme={theme} />}
        {program?.show_title && showTitle && (
          <div className="absolute" style={{ top: 44, left: 48, background: theme.panel, color: theme.text, padding: "8px 18px", borderRadius: 999, fontSize: 18, fontWeight: 600, borderLeft: `4px solid ${theme.accent}` }} data-testid="program-title">
            {showTitle}
          </div>
        )}
        <QuestionCard q={program?.question} theme={theme} bottom={overlayBottom} />
        <LowerThird lt={program?.lower_third} plate={program?.plate_style} autoSeconds={program?.plate_auto?.seconds || 6} theme={theme} bottom={overlayBottom} questionOn={!!(program?.question?.visible && program?.question?.text)} />
        <Banner banner={program?.banner} theme={theme} />
      </div>
      <style>{PLATE_KEYFRAMES}</style>
      {withAudio && meeting && <RtkParticipantsAudio meeting={meeting} />}
    </div>
  );
}
